import type {FC, ReactNode} from 'react'

export interface ThreeColumnLayoutProps {
  leftSidebar?: ReactNode
  mainContent: ReactNode
  rightSidebar?: ReactNode
  leftSidebarTopPadding?: string
  rightSidebarTopPadding?: string
  stickyTopOffset?: string
  mainContentClassName?: string
  gap?: string
}

/**
 * Three-column layout component for article-style pages
 * Used by BlogPost and GlossaryTerm
 *
 * Layout structure:
 * - Left sidebar (optional): Table of Contents
 * - Center: Main content (article/definition)
 * - Right sidebar (optional): CTA Section
 */
export const ThreeColumnLayout: FC<ThreeColumnLayoutProps> = ({
  leftSidebar,
  mainContent,
  rightSidebar,
  leftSidebarTopPadding = 'pt-0',
  rightSidebarTopPadding = 'pt-0',
  stickyTopOffset = 'top-24',
  mainContentClassName = 'flex w-full max-w-[600px] flex-col gap-6 max-md:px-5 md:gap-8',
  gap = 'gap-8 lg:gap-12',
}) => (
  <div className={`flex ${gap}`}>
    {/* Left column: Table of Contents (desktop only, sticky) */}
    {leftSidebar && (
      <aside
        className={`relative w-[220px] shrink-0 overflow-visible pl-6 ${leftSidebarTopPadding} max-[1100px]:hidden`}
      >
        <div className={`sticky ${stickyTopOffset} flex w-[196px] flex-col gap-8`}>{leftSidebar}</div>
      </aside>
    )}

    {/* Center column: Main content */}
    <div className={mainContentClassName}>{mainContent}</div>

    {/* Right column: CTA Section (desktop only, sticky) */}
    {rightSidebar && (
      <aside
        className={`relative w-[220px] shrink-0 overflow-visible pr-6 ${rightSidebarTopPadding} max-[1100px]:hidden`}
      >
        <div className={`sticky ${stickyTopOffset} flex w-[196px] flex-col`}>{rightSidebar}</div>
      </aside>
    )}
  </div>
)
